import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { toast } from "react-toastify";
import DataTable from "../../../components/shared/DataTable";
import SearchInput from "../../../components/shared/SearchInput";
import Pagination from "../../../components/shared/Pagination";
import { statusBadgeClass, formatDate } from "../../../utils/helpers";
import api from "../../../utils/api";

export default function DoctorList() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("all");

  const { data, isLoading } = useQuery(
    ["admin-doctors", page, search, statusFilter],
    () =>
      api.get(
        `/admin/doctors?page=${page}&search=${search}&status=${statusFilter}`
      ),
    { keepPreviousData: true }
  );

  const approveMutation = useMutation(
    (id) => api.post(`/admin/doctors/${id}/approve`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("admin-doctors");
        toast.success("Doctor approved");
      },
    }
  );

  const rejectMutation = useMutation(
    (id) => api.post(`/admin/doctors/${id}/reject`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("admin-doctors");
        toast.success("Doctor rejected");
      },
    }
  );

  const columns = [
    { key: "name", label: "Name", sortable: true },
    { key: "specialization", label: "Specialization", sortable: true },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    {
      key: "createdAt",
      label: "Registered On",
      render: (row) => formatDate(row.createdAt),
      sortable: true,
    },
    {
      key: "status",
      label: "Status",
      render: (row) => (
        <span className={statusBadgeClass(row.status)}>{row.status}</span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      render: (row) =>
        row.status === "pending" ? (
          <div className="flex gap-2">
            <button
              onClick={() => approveMutation.mutate(row.id)}
              className="text-xs px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors"
            >
              Approve
            </button>
            <button
              onClick={() => rejectMutation.mutate(row.id)}
              className="text-xs px-3 py-1 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors"
            >
              Reject
            </button>
          </div>
        ) : (
          <span className="text-slate-400 text-xs">No action</span>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search by name, specialization…"
          className="w-72"
        />
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="input-field w-auto"
        >
          <option value="all">All Statuses</option>
          <option value="pending">Pending</option>
          <option value="approved">Approved</option>
          <option value="rejected">Rejected</option>
        </select>
      </div>

      {/* Table */}
      <DataTable
        columns={columns}
        data={data?.data || []}
        loading={isLoading}
        emptyMessage="No doctors found."
      />

      <Pagination
        page={page}
        totalPages={data?.totalPages || 1}
        onPageChange={setPage}
      />
    </div>
  );
}
